import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnInit } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { ITaxWithheld } from "../../entities/taxes.model";
import { TaxesRemote } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { TransactionWrapperModalComponent } from "@modules/transaction/modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { EMPTY, map, Observable, retry } from "rxjs";
import { FiatTransaction } from "@models/transaction";
import { InfoService } from "@core-services/info.service";
import { ErrorClass } from "@models";
import { SegmentService } from "@main-services/segment.service";
import {
	OrderTypeTrackingProperty,
	TradeTrackingName,
	TradeTypeTrackingProperty
} from "@modules/checkout/entities/trade.rudderstack.tracking.model";
import { RudderstackService } from "@main-services/rudderstack.service";

@Component({
	selector: "tax-success-amount-label-ui",
	templateUrl: "./tax-success-amount-label.component.html",
	styleUrls: ["./tax-success-amount-label.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class TaxSuccessAmountLabelComponent implements OnInit {
	private modalService = inject(ModalService);
	private taxesRemote = inject(TaxesRemote);
	private infoService = inject(InfoService);
	private segmentService = inject(SegmentService);
	private rudderstackService = inject(RudderstackService);
	private changeRef = inject(ChangeDetectorRef);

	@Input() taxWithheld: Partial<ITaxWithheld> = null;
	@Input() type: "FIAT" | "ASSET" = "FIAT";
	@Input() isMarginTrading: boolean = false;

	taxInfoDetailVM$: Observable<FiatTransaction> = EMPTY;
	isloading = false;

	ngOnInit(): void {
		const { transactionId, fiatWalletId } = this.taxWithheld;

		this.taxInfoDetailVM$ = this.taxWithheld?.isSwapBCI
			? this.getTXDetailBCI(fiatWalletId, transactionId)
			: this.getTXDetail(fiatWalletId, transactionId);
	}

	public openTaxesInfo(): void {
		this.segmentService.track({
			name: TradeTrackingName.TradeSuccessScreenTaxWithheldCTATapped,
			properties: {
				trade_type: TradeTypeTrackingProperty.sell,
				order_type: this.isMarginTrading
					? OrderTypeTrackingProperty.margin_order
					: OrderTypeTrackingProperty.market_order
			}
		});
		this.rudderstackService.track({
			name: TradeTrackingName.TradeSuccessScreenTaxWithheldCTATapped,
			properties: {
				trade_type: TradeTypeTrackingProperty.sell,
				order_type: this.isMarginTrading
					? OrderTypeTrackingProperty.margin_order
					: OrderTypeTrackingProperty.market_order
			}
		});

		this.isloading = true;
		this.changeRef.markForCheck();

		this.taxInfoDetailVM$
			.pipe(
				retry({
					count: 2,
					delay: 1000
				})
			)
			.subscribe({
				next: (transaction) => {
					this.isloading = false;
					this.changeRef.markForCheck();

					this.modalService
						.create(TransactionWrapperModalComponent, {
							transaction,
							canOpen: false,
							closeAllPopups: true
						})
						.present();
				},
				error: (error) => {
					if (error instanceof ErrorClass) {
						this.infoService.setError(error);
					}
					this.isloading = false;
					this.changeRef.markForCheck();
				}
			});
	}

	private getTXDetail(fiatWalletId: string, transactionId: string): Observable<FiatTransaction> {
		return this.taxesRemote.getTransactionDetailRemote(fiatWalletId, transactionId);
	}

	//Remote call to get transaction detail for BCI swap is necessary because we need to check first if the transaction through BCI is a swap
	private getTXDetailBCI(fiatWalletId: string, transactionId: string): Observable<FiatTransaction> {
		return this.taxesRemote.getTransactionDetailBCIForSwapRemote(fiatWalletId, transactionId).pipe(
			map(() => {
				const transaction = new FiatTransaction();
				transaction.setId(transactionId);
				transaction.setCurrencyId(this.taxWithheld.assetId);
				transaction.setWalletId(fiatWalletId);
				transaction.setAmount(+this.taxWithheld.fiatAmountBeforeTax || 0);
				transaction.setType("sell");
				transaction.setIsIndex(true);
				transaction.setFiatId(this.taxWithheld.fiatId);
				transaction["is_swap"] = true; //this is the only hack to make set is_swap to true

				return transaction;
			})
		);
	}
}
